import type {
  Die,
  DieId,
  SinChoice,
  SinType,
} from "@cloudnest/carnival-of-sins-engine";
import { resolveSinChoice } from "@cloudnest/carnival-of-sins-engine";
import type { CarnivalOfSinsReplaySnapshot } from "./replay-runner";

export const SIN_LABELS: Readonly<Record<SinType, string>> = {
  greed: "Greed · 탐욕",
  gluttony: "Gluttony · 폭식",
  envy: "Envy · 질투",
  lust: "Lust · 색욕",
  pride: "Pride · 오만",
  wrath: "Wrath · 분노",
  sloth: "Sloth · 나태",
};

function diceOwnedBy(
  dice: readonly Die[],
  playerId: string,
): ReadonlySet<DieId> {
  return new Set(
    dice.filter((d) => d.lockedBy?.ownerId === playerId).map((d) => d.id),
  );
}

/**
 * Describes what a reveal actually took, resolved against the dice as they
 * stood *before* this action (`prev.state.dice`) — never the post-action
 * `state.dice`. When a reveal is the last of a round, the engine's
 * `cascadeRoundEnd` immediately rolls fresh dice for the next round inside
 * that same action's resulting state, so diffing against the post-action
 * state silently loses every round-ending reveal's outcome. Re-resolving the
 * choice with the engine's own pure `resolveSinChoice` against the known-good
 * "before" dice sidesteps that entirely and always matches what the engine
 * actually did.
 */
export function describeRevealOutcome(
  prev: CarnivalOfSinsReplaySnapshot | undefined,
  actorId: string,
  choice: SinChoice,
): string {
  if (choice.sin === "pride")
    return "라운드 종료 시 남은 주사위 전부 획득 예정";
  if (choice.sin === "envy") return "데빌 카드 보유자 점수 복사";
  if (choice.sin === "wrath") return "대상 주사위 재굴림";
  if (!prev) return "";

  const before = diceOwnedBy(prev.state.dice, actorId);
  const resolved = resolveSinChoice(
    prev.state.rngState,
    prev.state.dice,
    actorId,
    prev.state.seatingOrder,
    choice,
  );
  const gained = [...diceOwnedBy(resolved.dice, actorId)].filter(
    (id) => !before.has(id),
  );
  return gained.length > 0 ? `${gained.join(" + ")} 획득` : "가져갈 것 없음";
}

export function eventSummary(
  namesByPlayerId: Record<string, string>,
  snapshots: readonly CarnivalOfSinsReplaySnapshot[],
  index: number,
  showRecap: boolean,
): string {
  const current = snapshots[index];
  if (!current) return "";
  const event = current.event;
  const name = namesByPlayerId[event.userId] ?? event.userId;
  if (event.eventType === "init") return `#${event.sequenceNumber} 게임 시작`;

  if (event.eventType === "SelectSin") {
    const sin = (event.payload as { sin?: SinType }).sin;
    if (!showRecap || !sin)
      return `#${event.sequenceNumber} ${name} · 비밀 선택 완료`;
    return `#${event.sequenceNumber} ${name} · ${SIN_LABELS[sin]} 선택(복기)`;
  }

  const choice =
    current.action?.type === "RevealSin" ? current.action.choice : null;
  if (!choice) return `#${event.sequenceNumber} ${name} 공개`;
  const outcome = describeRevealOutcome(
    snapshots[index - 1],
    event.userId,
    choice,
  );
  return `#${event.sequenceNumber} ${name} 공개 → ${SIN_LABELS[choice.sin]} · ${outcome}`;
}
